import { Box, Button, Container, FormControl, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import Loading from '../../components/Loading';
import { useAppDispatch } from '../../store/hooks';
import { useAddUserMutation, useFindUserByIdMutation, useUpdateUserMutation } from '../../store/api/userApi';
import { useGetRolesMutation } from '../../store/api/roleApi';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

type Props = {};

const customerSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter full name')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' }),
  phoneNo: Yup.string()
    .required('Phone No is required')
    .min(10, 'Minimum 10 digits allowed')
    .max(10, 'Maximum 10 digits allowed'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().required('Password is required').min(6, 'Password is too short'),
  role: Yup.string().required('User Role is required'),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const AddUser = (props: Props) => {
  const localUserObj = JSON.parse(localStorage.getItem('login') || '{}');
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    name: '',
    phoneNo: 0,
    email: '',
    password: '',
    role: '',
  });
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const [findByUser, findByUserResponse] = useFindUserByIdMutation();
  const [addUser, addUserResponse] = useAddUserMutation();
  const [updateUser, updateUserResponse] = useUpdateUserMutation();
  const [getRole, getRoleResponse] = useGetRolesMutation();
  const [newRole, setNewRole] = useState([]) as any;
  const [variantName, setVariantName] = React.useState([]) as any;
  const [roleId, setRoleId] = useState([]) as any;

  // set formik initial values
  useEffect(() => {
    if (state.editMode) {
      findByUser({
        token: token,
        id: state.id,
      });
    }
  }, [state.editMode]);

  useEffect(() => {
    getRole({
      token: token,
      id: state.id,
      merchantId:
        localUserObj.userType === 'UTSP'
          ? process.env.REACT_APP_MERCHANT_ID_FOR_VENDOR
          : process.env.REACT_APP_MERCHANT_ID_FOR_ADMIN,
    });
  }, []);

  useEffect(() => {
    if (getRoleResponse.isSuccess) {
      setNewRole(getRoleResponse.data);
      getAllRoleId(getRoleResponse.data);
    }
  }, [getRoleResponse.isSuccess]);

  useEffect(() => {
    if (findByUserResponse.isSuccess) {
      setInitialValues({
        name: findByUserResponse.data?.name,
        phoneNo: parseInt(findByUserResponse.data?.mobileNo) || 0,
        email: findByUserResponse.data?.email,
        password: findByUserResponse.data?.password,
        role: findByUserResponse.data?.roleIds[0],
      });
      setRoleId(findByUserResponse.data?.roleIds);
    }
  }, [findByUserResponse.isSuccess]);

  function getAllRoleId(newRole: any) {
    let parkingId: any = [];
    for (let i = 0; i < newRole.length; i++) {
      for (let j = 0; j < roleId.length; j++) {
        if (newRole[i]?.id === roleId[j]) {
          parkingId.push(newRole[i]);
        }
      }
    }
    setVariantName(parkingId);
  }

  const saveCustomerDetailDataToServer = async (values: any) => {
    addUser({
      payload: {
        categoryTypeId: localUserObj.userType === 'UTSA' ? 'UTSA' : 'UTSP',
        companyId: 0,
        deviceId: '',
        dob: '',
        email: values.email.trim(),
        id: 0,
        merchantId: token.merchantId,
        mobileNo: values.phoneNo,
        name: values.name.trim(),
        password: values.password,
        registrationCompleted: true,
        registrationStatus: true,
        status: true,
        termsAndCondition: true,
        userId: 0,
        username: values.email.trim(),
        roleIds: [values.role],
        serviceProviderId: localUserObj?.serviceProviderId ? localUserObj?.serviceProviderId : '',
      },
      token: token,
    });
  };

  useEffect(() => {
    if (addUserResponse.isSuccess) {
      toast.success('User added successfully');
      navigate('/dashboard/user-management');
    } else if (addUserResponse.isError) {
      let errorData: any = addUserResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error(msg || '');
    }
  }, [addUserResponse.isSuccess, addUserResponse.isError]);

  const updateCustomerDetailDataInServer = async (values: any) => {
    await updateUser({
      payload: {
        categoryTypeId: localUserObj.userType === 'UTSA' ? 'UTSA' : 'UTSP',
        companyId: 0,
        deviceId: '',
        dob: '',
        email: values.email,
        id: state.id,
        merchantId: token.merchantId,
        mobileNo: values.phoneNo,
        name: values.name.trim(),
        password: values.password,
        registrationCompleted: true,
        registrationStatus: true,
        status: true,
        termsAndCondition: true,
        userId: 0,
        username: values.username,
        roleIds: [values.role],
        serviceProviderId: localUserObj?.serviceProviderId ? localUserObj?.serviceProviderId : '',
      },
      id: state.id,
      token: token,
    });
  };

  useEffect(() => {
    if (updateUserResponse.isSuccess) {
      toast.success('User updated successfully');
      navigate('/dashboard/user-management');
    } else if (updateUserResponse.isError) {
      let errorData: any = updateUserResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error(msg || '');
    }
  }, [updateUserResponse.isSuccess, updateUserResponse.isError]);

  const handleAddNewServiceProvider = (values: any, action: any) => {
    if (state.editMode) {
      updateCustomerDetailDataInServer(values);
    } else {
      saveCustomerDetailDataToServer(values);
    }
    action.resetForm();
  };

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit User' : 'Add User'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddNewServiceProvider}
              validationSchema={customerSchema}
            >
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, touched, errors }) => (
                <Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Name
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="name"
                          style={{
                            width: '80%',
                          }}
                          type="text"
                          name="name"
                          label="Enter full name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Stack>
                    </Box>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="phoneNo"
                          style={{
                            width: '80%',
                          }}
                          type="number"
                          name="phoneNo"
                          label="Enter Phone Number"
                          value={values.phoneNo}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.phoneNo && errors.phoneNo)}
                          helperText={touched.phoneNo && errors.phoneNo}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Box display={'flex'} alignItems={'center'} marginTop={'25px'} marginLeft={'25px'}>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Email Address
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="email"
                          style={{
                            width: '80%',
                          }}
                          type="text"
                          name="email"
                          label="Enter email address"
                          value={values.email}
                          onChange={handleChange}
                          disabled={state.editMode}
                          onBlur={handleBlur}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Stack>
                    </Box>
                    <Box width={'100%'}>
                      <Stack direction="column" alignItems="self-start">
                        <Typography variant="h5" gutterBottom component="div">
                          Password
                        </Typography>
                        <TextField
                          fullWidth
                          autoComplete="password"
                          style={{
                            width: '80%',
                          }}
                          type="password"
                          name="password"
                          label="Enter password"
                          value={values.password}
                          disabled={state.editMode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.password && errors.password)}
                          helperText={touched.password && errors.password}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Box marginTop={'25px'} marginLeft={'25px'}>
                    <Typography variant="h5" gutterBottom component="div">
                      User Role
                    </Typography>
                    <FormControl sx={{ width: '90%' }}>
                      <Select
                        name="newrole"
                        id="role"
                        value={values.role ? values.role : ''}
                        className="w-[300px] md:w-[500px]"
                        onChange={(event: any) => {
                          setFieldValue('role', event.target.value);
                        }}
                        error={Boolean(touched.role && errors.role)}
                        displayEmpty
                        defaultValue={''}
                      >
                        <MenuItem disabled value="">
                          Select User Role
                        </MenuItem>
                        {newRole &&
                          newRole.map((newrole: any) => (
                            <MenuItem key={newrole.id} value={newrole.id}>
                              {newrole.name}
                            </MenuItem>
                          ))}
                      </Select>
                      {errors.role && (
                        <div style={{ color: 'red' }} className="text-sm">
                          {errors.role}
                        </div>
                      )}
                    </FormControl>
                  </Box>
                  <Box style={{ width: '100%', display: 'flex', marginLeft: '75%', marginTop: '20px' }}>
                    <Button
                      style={{
                        color: 'white',
                        backgroundColor: '#E23744',
                      }}
                      variant="contained"
                      color="error"
                      onClick={() => {
                        navigate('/dashboard/user-management');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      style={{
                        color: 'white',
                        backgroundColor: '#E23744',
                      }}
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      size="large"
                      type="submit"
                    >
                      {state.editMode ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {findByUserResponse.isLoading ? <Loading /> : null}
    </Box>
  );
};

export default AddUser;
